/** Tiny typed event bus decoupling game logic from UI and rendering. */
export interface Events {
  /** Some part of the game state changed; UI should refresh lazily. */
  change: void;
  /** Short message at the top of the screen. */
  toast: string;
  /** Floating text above the player (rewards, hints). */
  float: { text: string; color: string };
  /** A burst of particles at the player (sparkles, hearts...). */
  burst: { kind: 'sparkle' | 'heart' | 'note' | 'drop'; count?: number };
  /** The day ended (bedtime or skipped). */
  dayEnd: void;
  /** A new day started (after the player is placed). */
  dayStart: void;
  /** An objective was completed. */
  objective: string;
}

type Handler<T> = (payload: T) => void;

class Bus {
  private handlers = new Map<keyof Events, Set<Handler<never>>>();

  on<K extends keyof Events>(type: K, fn: Handler<Events[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) this.handlers.set(type, (set = new Set()));
    set.add(fn as Handler<never>);
    return () => set!.delete(fn as Handler<never>);
  }

  emit<K extends keyof Events>(type: K, ...payload: Events[K] extends void ? [] : [Events[K]]): void {
    this.handlers.get(type)?.forEach((fn) => (fn as Handler<Events[K]>)(payload[0] as Events[K]));
  }
}

export const bus = new Bus();
